import {
  DeleteOutlined,
  EditOutlined,
  KeyOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Badge, Button, Descriptions, Divider, Drawer, Empty, Form, Input, Modal, message, Popconfirm, Radio, Space, Tag, Typography } from 'antd';
import { createStyles } from 'antd-style';
import React, { useEffect, useState } from 'react';
import { getErrorDetail, useEnderRealmClient } from '@/services/enderrealm/client';
import { getEnvironmentIdentity } from '@/services/enderrealm/environment';
import { useEnvironment } from './EnvironmentContext';
import type { EnvironmentConfig, HealthStatus } from './types';

const { Text, Title } = Typography;

const HEALTH_META: Record<HealthStatus, { label: string; status: 'success' | 'error' | 'processing' | 'default' }> = {
  online: { label: '在线', status: 'success' },
  offline: { label: '离线', status: 'error' },
  checking: { label: '检测中', status: 'processing' },
  unknown: { label: '未检测', status: 'default' },
};

const useStyles = createStyles(({ token }) => ({
  drawerBody: {
    overflowY: 'auto',
  },
  envList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  envItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadius,
    cursor: 'pointer',
    transition: 'all 0.3s',
    '&:hover': {
      backgroundColor: token.colorBgTextHover,
    },
    ['&.active']: {
      borderColor: token.colorPrimary,
      backgroundColor: token.colorPrimaryBg,
    },
  },
  dot: {
    flexShrink: 0,
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    border: '1px solid rgba(0,0,0,0.15)',
    display: 'inline-block',
    ['&[data-status="online"]']: {
      backgroundColor: '#52c41a',
      boxShadow: '0 0 6px rgba(82, 196, 26, 0.6)',
    },
    ['&[data-status="offline"]']: {
      backgroundColor: '#ff4d4f',
      boxShadow: '0 0 6px rgba(255, 77, 79, 0.6)',
    },
    ['&[data-status="checking"]']: {
      backgroundColor: '#faad14',
      animation: 'er-console-env-blink 1s ease-in-out infinite',
    },
    ['&[data-status="unknown"]']: {
      backgroundColor: '#8c8c8c',
    },
  },
  envItemMain: {
    flex: 1,
    minWidth: 0,
  },
  envItemTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '2px',
  },
  envName: {
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  envItemSub: {
    fontSize: '12px',
    color: token.colorTextSecondary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  detailCard: {
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadius,
    padding: '12px',
  },
  keyRow: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
  },
}));

interface EnvFormValues {
  name: string;
  baseUrl: string;
  description?: string;
  apiKey?: string;
}

type ModalState = { mode: 'add' } | { mode: 'edit'; env: EnvironmentConfig } | null;

const EnvironmentPanel: React.FC = () => {
  const { styles, cx } = useStyles();
  const {
    environments,
    healthMap,
    activeId,
    activeEnvironment,
    apiKeys,
    setActiveEnv,
    setApiKey,
    addEnvironment,
    updateEnvironment,
    removeEnvironment,
    refreshHealth,
    panelOpen,
    closePanel,
  } = useEnvironment();

  const [keyDraft, setKeyDraft] = useState('');
  const [modalState, setModalState] = useState<ModalState>(null);
  const [envIdentity, setEnvIdentity] = useState<{
    environment?: string;
    server_name?: string | null;
    region?: string | null;
    custom_config?: Record<string, string>;
  } | null>(null);
  const [envIdentityError, setEnvIdentityError] = useState<string>();
  const [form] = Form.useForm<EnvFormValues>();

  const apiClient = useEnderRealmClient();

  // API Key 草稿跟随当前环境切换
  useEffect(() => {
    setKeyDraft(activeId ? apiKeys[activeId] ?? '' : '');
  }, [activeId, apiKeys]);

  // 打开面板时读取当前环境的标识信息
  useEffect(() => {
    let cancelled = false;
    setEnvIdentity(null);
    setEnvIdentityError(undefined);
    if (!panelOpen || !activeEnvironment || !apiClient) return;
    getEnvironmentIdentity(apiClient)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setEnvIdentityError(getErrorDetail(error));
          return;
        }
        if (data) setEnvIdentity(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setEnvIdentityError(getErrorDetail(err));
      });
    return () => {
      cancelled = true;
    };
  }, [panelOpen, activeEnvironment, apiClient]);

  const activeHealth = activeEnvironment ? healthMap[activeEnvironment.id] : undefined;
  const activeStatus: HealthStatus = activeHealth?.status ?? 'unknown';
  const activeMeta = HEALTH_META[activeStatus];

  const handleSaveKey = () => {
    if (!activeEnvironment) return;
    setApiKey(activeEnvironment.id, keyDraft);
    message.success('API Key 已保存');
  };

  const openAdd = () => {
    form.resetFields();
    form.setFieldsValue({ baseUrl: 'https://' });
    setModalState({ mode: 'add' });
  };

  const openEdit = (env: EnvironmentConfig) => {
    form.setFieldsValue({
      name: env.name,
      baseUrl: env.baseUrl,
      description: env.description,
      apiKey: apiKeys[env.id] ?? '',
    });
    setModalState({ mode: 'edit', env });
  };

  const handleModalOk = async () => {
    const values = await form.validateFields();
    if (modalState?.mode === 'edit') {
      updateEnvironment(modalState.env.id, {
        name: values.name,
        baseUrl: values.baseUrl,
        description: values.description,
      });
      setApiKey(modalState.env.id, values.apiKey ?? '');
      message.success('环境已更新');
    } else {
      addEnvironment({
        name: values.name,
        baseUrl: values.baseUrl,
        description: values.description,
        apiKey: values.apiKey,
      });
      message.success('环境已添加');
    }
    setModalState(null);
  };

  const handleDelete = (env: EnvironmentConfig) => {
    removeEnvironment(env.id);
    message.success(`已删除环境「${env.name}」`);
  };

  return (
    <>
      <Drawer
        title={
          <Space>
            <KeyOutlined />
            <span>环境配置</span>
          </Space>
        }
        open={panelOpen}
        onClose={closePanel}
        width={440}
      >
        <div className={styles.drawerBody}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            选择当前使用的 EnderRealm 服务器环境，并为每个环境配置 API Key。添加的环境保存在本机浏览器中。
          </Text>

          <Divider orientation="left" style={{ margin: '16px 0 12px' }}>
            环境列表
          </Divider>

          <div className={styles.envList}>
            {environments.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="暂无环境，请先通过环境变量或手动添加"
              />
            ) : (
              environments.map((env) => {
                const health = healthMap[env.id];
                const status: HealthStatus = health?.status ?? 'unknown';
                const isActive = env.id === activeId;
                const hasKey = !!apiKeys[env.id];
                return (
                  <div
                    key={env.id}
                    className={cx(styles.envItem, isActive && 'active')}
                    onClick={() => setActiveEnv(env.id)}
                  >
                    <Radio checked={isActive} />
                    <span className={styles.dot} data-status={status} />
                    <div className={styles.envItemMain}>
                      <div className={styles.envItemTitle}>
                        <span className={styles.envName}>{env.name}</span>
                        <Tag style={{ marginInlineEnd: 0 }}>{env.source === 'builtin' ? '环境变量' : '自定义'}</Tag>
                        {!hasKey && <Tag color="warning">未配置 Key</Tag>}
                      </div>
                      <div className={styles.envItemSub}>
                        {env.baseUrl}
                        {health?.latencyMs != null && ` · ${health.latencyMs}ms`}
                      </div>
                    </div>
                    <div>
                      {env.source === 'local' && (
                        <Space size={4}>
                          <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              openEdit(env);
                            }}
                          />
                          <Popconfirm
                            title="删除该环境？"
                            description="将同时移除其 API Key，且不可恢复。"
                            onConfirm={() => handleDelete(env)}
                          >
                            <Button
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Popconfirm>
                        </Space>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <Button
            type="dashed"
            block
            icon={<PlusOutlined />}
            onClick={openAdd}
            style={{ marginTop: '12px' }}
          >
            添加环境
          </Button>

          <Divider orientation="left" style={{ margin: '20px 0 12px' }}>
            当前环境详情
          </Divider>

          {activeEnvironment ? (
            <div className={styles.detailCard}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '8px',
                }}
              >
                <Title level={5} style={{ margin: 0 }}>
                  {activeEnvironment.name}
                </Title>
                <Space>
                  <Badge status={activeMeta.status} text={activeMeta.label} />
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    loading={activeStatus === 'checking'}
                    onClick={() => refreshHealth(activeEnvironment.id)}
                  >
                    检测
                  </Button>
                </Space>
              </div>

              <Descriptions size="small" column={1}>
                <Descriptions.Item label="环境标识">
                  {envIdentity ? (
                    <Text>
                      {envIdentity.environment || '未知环境'}
                      {envIdentity.server_name ? ` · ${envIdentity.server_name}` : ''}
                      {envIdentity.region ? ` · ${envIdentity.region}` : ''}
                    </Text>
                  ) : envIdentityError ? (
                    <Text type="warning" style={{ fontSize: '12px' }}>
                      环境信息读取失败：{envIdentityError}
                    </Text>
                  ) : (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      读取中…
                    </Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="地址">
                  <Text copyable style={{ wordBreak: 'break-all' }}>
                    {activeEnvironment.baseUrl}
                  </Text>
                </Descriptions.Item>
                {activeEnvironment.description && (
                  <Descriptions.Item label="说明">{activeEnvironment.description}</Descriptions.Item>
                )}
                <Descriptions.Item label="来源">
                  {activeEnvironment.source === 'builtin' ? '环境变量（构建期配置）' : '自定义（本地保存）'}
                </Descriptions.Item>
              </Descriptions>

              <Divider style={{ margin: '12px 0' }} />

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '8px',
                }}
              >
                <Text strong>API Key</Text>
                {activeHealth?.detail && (
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {activeHealth.detail}
                  </Text>
                )}
              </div>
              <div className={styles.keyRow}>
                <Input.Password
                  placeholder="输入该环境的 API Key（er_ 开头）"
                  value={keyDraft}
                  onChange={(e) => setKeyDraft(e.target.value)}
                  onPressEnter={handleSaveKey}
                />
                <Button type="primary" onClick={handleSaveKey}>
                  保存
                </Button>
              </div>

              {activeEnvironment.source === 'local' && (
                <div style={{ marginTop: '12px', textAlign: 'right' }}>
                  <Space>
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => openEdit(activeEnvironment)}
                    >
                      编辑环境
                    </Button>
                    <Popconfirm
                      title="删除该环境？"
                      description="将同时移除其 API Key，且不可恢复。"
                      onConfirm={() => handleDelete(activeEnvironment)}
                    >
                      <Button danger icon={<DeleteOutlined />}>
                        删除环境
                      </Button>
                    </Popconfirm>
                  </Space>
                </div>
              )}
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="未选择环境" />
          )}
        </div>
      </Drawer>

      <Modal
        title={modalState?.mode === 'edit' ? '编辑环境' : '添加环境'}
        open={modalState !== null}
        onOk={handleModalOk}
        onCancel={() => setModalState(null)}
        okText="保存"
        cancelText="取消"
        width={460}
      >
        <Form form={form} layout="vertical" style={{ marginTop: '16px' }}>
          <Form.Item
            name="name"
            label="环境名称"
            rules={[{ required: true, message: '请输入环境名称，如 正式服' }]}
          >
            <Input placeholder="例如：正式服 / 测试服" maxLength={40} />
          </Form.Item>
          <Form.Item
            name="baseUrl"
            label="Base URL"
            rules={[
              { required: true, message: '请输入后端 API 基础地址' },
              {
                pattern: /^https?:\/\/.+/,
                message: '需以 http:// 或 https:// 开头',
              },
            ]}
          >
            <Input placeholder="例如：https://api.enderrealm.cn" />
          </Form.Item>
          <Form.Item name="description" label="说明（可选）">
            <Input.TextArea placeholder="例如：线上环境 / 开发环境" autoSize={{ minRows: 2, maxRows: 4 }} maxLength={200} />
          </Form.Item>
          <Form.Item name="apiKey" label="API Key（可选）">
            <Input.Password placeholder="er_ 开头的 API Key，留空则不设置" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default EnvironmentPanel;